import { NEW_BASE_URL } from "../config";
import { generalHeader } from "../cycles/headers";

/**
 * BlogService - Singleton service for managing blog articles and catalogues
 *
 * This service provides a centralized interface for fetching and managing
 * blog articles from the asset management content API. It handles article
 * catalogues, individual articles, categories, and search functionality.
 */
class BlogService {
  constructor() {
    // State management
    this.articleCatalogue = null;
    this.currentArticle = null;
    this.categoryList = null;
    this.searchCache = new Map();
    this.catalogueCache = new Map();

    // Configuration for compatibility with BlogService
    this.application = null;
    this.userId = null;
    this.csrfToken = null;
  }

  /**
   * Set the application ID for all requests (BlogService compatibility)
   * @param {string} applicationId - The application ID to use in headers
   */
  setApplication(applicationId) {
    this.application = applicationId;
  }

  /**
   * Set the user ID for all requests (BlogService compatibility)
   * @param {string} userId - The user ID to use in headers
   */
  setUserId(userId) {
    this.userId = userId;
  }

  /**
   * Set the CSRF token for all requests (BlogService compatibility)
   * @param {string} csrfToken - The CSRF token to use in headers
   */
  setCsrfToken(csrfToken) {
    this.csrfToken = csrfToken;
  }

  /**
   * Creates a request header for the content API
   * @private
   */
  createRequestHeader(userId) {
    return {
      id: this.generateGuid(),
      application: this.application || "03FC0B90-DFAD-11EE-8D86-0800200C9A66",
      userId: userId || this.userId || "00000000-0000-0000-0000-000000000000",
    };
  }

  /**
   * Generates a GUID
   * @private
   */
  generateGuid() {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }
    );
  }

  /**
   * Fetches article catalogue with pagination
   * @param {number} catalogueNumber - The catalogue page number (optional)
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for backend authentication
   * @returns {Promise<Object>} Article catalogue with articles and pagination
   */
  async getArticleCatalogue(catalogueNumber, userId, csrfToken) {
    const cacheKey = catalogueNumber || "default";

    // Check cache first
    if (this.catalogueCache.has(cacheKey)) {
      return this.catalogueCache.get(cacheKey);
    }

    try {
      const requestBody = {
        header: {
          userId: userId || "00000000-0000-0000-0000-000000000000",
          bank: "NBG",
        },
        payload: catalogueNumber ? { catalogueNumber } : {},
      };

      const response = await fetch(`${NEW_BASE_URL}/Blog/catalogue/search`, {
        method: "POST",
        headers: generalHeader(csrfToken, true),
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Backend wraps response in { payload: ... }
      const catalogueData = data.payload || data;

      // Cache the result
      this.catalogueCache.set(cacheKey, catalogueData);
      this.articleCatalogue = catalogueData;

      // Extract categories from articles if not already done
      if (!this.categoryList && catalogueData.articles) {
        this.extractCategoriesFromArticles(catalogueData.articles);
      }

      return catalogueData;
    } catch (error) {
      console.error("Error fetching article catalogue:", error);
      throw error;
    }
  }

  /**
   * Searches for articles with filters
   * @param {string} searchTerm - Search term for title/content
   * @param {Array} categories - Category filter array
   * @param {number} page - Page number (0-based)
   * @param {number} itemsPerPage - Items per page
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for backend authentication
   * @returns {Promise<Object>} Search results with articles and metadata
   */
  async searchArticles(
    searchTerm,
    categories,
    page,
    itemsPerPage,
    userId,
    csrfToken
  ) {
    // Create cache key WITHOUT page number for the full dataset
    const dataKey = `${searchTerm}_${categories.join(",")}_data`;

    let filteredArticles;
    let allArticles;

    // Check if we have the full dataset cached
    if (this.searchCache.has(dataKey)) {
      filteredArticles = this.searchCache.get(dataKey);
      // Also get all articles for category extraction
      allArticles = this.searchCache.get("_all_articles_") || filteredArticles;
    } else {
      try {
        // Fetch all articles once
        const requestBody = {
          header: {
            userId: userId || "00000000-0000-0000-0000-000000000000",
            bank: "NBG",
          },
          payload: {}, // No catalogueNumber to get all available articles
        };

        const response = await fetch(`${NEW_BASE_URL}/Blog/catalogue/search`, {
          method: "POST",
          headers: generalHeader(csrfToken, true),
          credentials: "include",
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Backend wraps response in { payload: ... }
        const catalogueData = data.payload || data;
        // Get all articles first
        allArticles = catalogueData.articles || [];

        // Cache all articles for category extraction
        this.searchCache.set("_all_articles_", allArticles);

        // Apply client-side filtering for search term and categories
        filteredArticles = [...allArticles];

        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          filteredArticles = filteredArticles.filter(
            (article) =>
              article.title?.toLowerCase().includes(searchLower) ||
              article.body?.toLowerCase().includes(searchLower) ||
              article.summary?.toLowerCase().includes(searchLower)
          );
        }

        if (categories && categories.length > 0) {
          filteredArticles = filteredArticles.filter((article) => {
            const articleCategories = article.categories || [];
            return categories.some((cat) =>
              articleCategories.some(
                (artCat) =>
                  artCat.name === cat ||
                  artCat.id === cat ||
                  artCat.uuid === cat
              )
            );
          });
        }

        // Cache the filtered full dataset
        this.searchCache.set(dataKey, filteredArticles);
      } catch (error) {
        console.error("Error fetching articles:", error);
        throw error;
      }
    }

    // Extract categories from ALL articles (not filtered)
    const extractedCategories = this.extractCategoriesFromArticles(allArticles);

    // Apply pagination to the cached filtered results
    const startIndex = page * itemsPerPage;
    const paginatedArticles = filteredArticles.slice(
      startIndex,
      startIndex + itemsPerPage
    );

    const result = {
      articles: paginatedArticles,
      categories: extractedCategories, // Include categories in the result
      metadata: {
        total: filteredArticles.length,
        page: page,
        itemsPerPage: itemsPerPage,
        totalPages: Math.ceil(filteredArticles.length / itemsPerPage),
      },
    };

    return result;
  }

  /**
   * Fetches a single article by identifier
   * @param {string} identifier - Article fileName, URL, or title
   * @param {string} identifierType - Type of identifier: 'fileName', 'url', or 'title'
   * @param {string} author - Author name (required if using title)
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for backend authentication
   * @returns {Promise<Object>} Article details
   */
  async getArticle(
    identifier,
    identifierType = "fileName",
    author = null,
    userId,
    csrfToken
  ) {
    try {
      const payload = {};

      switch (identifierType) {
        case "fileName":
          payload.fileName = identifier;
          break;
        case "url":
          payload.url = identifier;
          break;
        case "title":
          if (!author) {
            throw new Error("Author is required when searching by title");
          }
          payload.title = identifier;
          payload.author = author;
          break;
        default:
          // Try to auto-detect the identifier type
          if (identifier.includes("/")) {
            payload.url = identifier;
          } else if (identifier.endsWith(".json") || identifier.includes("-")) {
            payload.fileName = identifier;
          } else {
            payload.title = identifier;
          }
      }

      const requestBody = {
        header: {
          userId: userId || "00000000-0000-0000-0000-000000000000",
          bank: "NBG",
        },
        payload,
      };

      const response = await fetch(`${NEW_BASE_URL}/Blog/article/search`, {
        method: "POST",
        headers: generalHeader(csrfToken, true),
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Backend wraps response in { payload: ... }
      const articleData = data.payload || data;

      // Store current article
      this.currentArticle = articleData;

      return articleData;
    } catch (error) {
      console.error("Error fetching article:", error);
      throw error;
    }
  }

  /**
   * Gets article by ID (URL or fileName)
   * @param {string} articleId - Article URL or fileName
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for backend authentication
   * @returns {Promise<Object>} Article details
   */
  async getArticleById(articleId, userId, csrfToken) {
    // Drupal aliases were case-insensitive, and old links (FAQs, other sites) use mixed case,
    // e.g. /news/Introducing-the-Go-Live-button for the post stored as introducing-the-go-live-button.
    const wanted = (articleId || "").toLowerCase();
    const isWanted = (art) =>
      (art.url || "").toLowerCase() === wanted ||
      (art.fileName || "").toLowerCase() === wanted;

    // First check if we have cached articles to find the correct fileName
    const cachedArticles = this.searchCache.get("_all_articles_");

    if (cachedArticles) {
      const article = cachedArticles.find(isWanted);

      if (article && article.fileName) {
        // Use fileName for fetching as it's more reliable
        return this.getArticle(
          article.fileName,
          "fileName",
          null,
          userId,
          csrfToken
        );
      }
    }

    // If not in cache, try to fetch the catalogue first
    if (!cachedArticles) {
      try {
        await this.getArticleCatalogue(null, userId, csrfToken);

        if (this.articleCatalogue && this.articleCatalogue.articles) {
          const article = this.articleCatalogue.articles.find(isWanted);

          if (article && article.fileName) {
            return this.getArticle(
              article.fileName,
              "fileName",
              null,
              userId,
              csrfToken
            );
          }
        }
      } catch (error) {
        console.error("Error fetching catalogue for article lookup:", error);
      }
    }

    // Last resort: try as URL first, then as fileName
    try {
      // Try as URL
      return await this.getArticle(articleId, "url", null, userId, csrfToken);
    } catch (error) {
      // If URL fails, try as fileName
      return this.getArticle(articleId, "fileName", null, userId, csrfToken);
    }
  }

  /**
   * Refreshes the article catalogue cache
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for backend authentication
   * @returns {Promise<Object>} Fresh article catalogue
   */
  async refreshCatalogue(userId, csrfToken) {
    try {
      const requestBody = {
        header: {
          userId: userId || "00000000-0000-0000-0000-000000000000",
          bank: "NBG",
        },
        payload: {},
      };

      const response = await fetch(`${NEW_BASE_URL}/Blog/catalogue/refresh`, {
        method: "POST",
        headers: generalHeader(csrfToken, true),
        credentials: "include",
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Clear caches
      this.catalogueCache.clear();
      this.searchCache.clear();
      this.articleCatalogue = null;

      // Fetch fresh catalogue
      return this.getArticleCatalogue(null, userId, csrfToken);
    } catch (error) {
      console.error("Error refreshing catalogue:", error);
      throw error;
    }
  }

  /**
   * Extracts categories from articles
   * @private
   * @param {Array} articles - Array of articles
   * @returns {Array} List of unique categories
   */
  extractCategoriesFromArticles(articles) {
    const categoryMap = new Map();

    articles.forEach((article) => {
      const categories = article.categories || [];
      categories.forEach((cat) => {
        // Use name as the unique identifier since id might not exist
        const key = cat.name || cat.id;
        if (key && !categoryMap.has(key)) {
          categoryMap.set(key, {
            id: cat.id || cat.name, // Use name as id if id doesn't exist
            name: cat.name || cat.id,
            uuid: cat.id || cat.name, // For backward compatibility
          });
        }
      });
    });

    const extractedCategories = Array.from(categoryMap.values()).sort((a, b) =>
      a.name.localeCompare(b.name)
    );

    this.categoryList = extractedCategories;
    return extractedCategories;
  }

  /**
   * Gets the list of article categories
   * @param {string} userId - User ID for authentication
   * @param {string} csrfToken - CSRF token for backend authentication
   * @returns {Promise<Array>} List of categories
   */
  async getCategories(userId, csrfToken) {
    if (this.categoryList) {
      return this.categoryList;
    }

    // Fetch catalogue to extract categories
    await this.getArticleCatalogue(null, userId, csrfToken);

    return this.categoryList || [];
  }

  /**
   * Gets the currently loaded article
   * @returns {Object|null} Current article or null
   */
  getCurrentArticle() {
    return this.currentArticle;
  }

  /**
   * Gets the current article catalogue
   * @returns {Object|null} Current catalogue or null
   */
  getCurrentCatalogue() {
    return this.articleCatalogue;
  }

  // Internal storage property for catalogueData (BlogService compatibility)
  catalogueData = null;

  /**
   * Clears all cached data
   */
  clear() {
    this.articleCatalogue = null;
    this.currentArticle = null;
    this.categoryList = null;
    this.searchCache.clear();
    this.catalogueCache.clear();
  }

  /**
   * Download article as JSON file (BlogService compatibility)
   * @param {Object} article - Article data to download
   * @param {string} filename - Optional filename
   */
  downloadArticle(article = null, filename = null) {
    const articleToDownload = article || this.currentArticle;

    if (!articleToDownload) {
      console.error("No article to download");
      return false;
    }

    const defaultFilename =
      filename ||
      `article_${articleToDownload.url || "document"}_${Date.now()}.json`;

    try {
      const dataStr = JSON.stringify(articleToDownload, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = defaultFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error downloading article:", error);
      return false;
    }
  }

  /**
   * Download catalogue as JSON file (BlogService compatibility)
   * @param {Array} catalogue - Catalogue data to download
   * @param {string} filename - Optional filename
   */
  downloadCatalogue(catalogue = null, filename = null) {
    const catalogueToDownload =
      catalogue || this.catalogueData || this.articleCatalogue;

    if (!catalogueToDownload) {
      console.error("No catalogue to download");
      return false;
    }

    const defaultFilename = filename || `blog_catalogue_${Date.now()}.json`;

    try {
      const dataStr = JSON.stringify(catalogueToDownload, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = defaultFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error downloading catalogue:", error);
      return false;
    }
  }

  // BlogService compatibility methods

  /**
   * Search for an article using various criteria (BlogService compatibility)
   * @param {Object} searchParams - Search parameters
   * @param {string} searchParams.fileName - Article file name (fastest)
   * @param {string} searchParams.url - Article URL
   * @param {string} searchParams.title - Article title (requires author)
   * @param {string} searchParams.author - Article author (requires title)
   * @returns {Promise<Object>} Article data
   */
  async searchArticle(searchParams) {
    const { fileName, url, title, author } = searchParams;

    // Validate search parameters
    if (!fileName && !url && !(title && author)) {
      throw new Error(
        "Please provide either fileName, url, or both title and author"
      );
    }

    let identifierType = "fileName";
    let identifier = fileName;

    if (fileName) {
      identifierType = "fileName";
      identifier = fileName;
    } else if (url) {
      identifierType = "url";
      identifier = url;
    } else if (title && author) {
      identifierType = "title";
      identifier = title;
    }

    const result = await this.getArticle(
      identifier,
      identifierType,
      author,
      this.userId,
      this.csrfToken
    );

    // Extract the article from the response if it's wrapped
    if (result && result.article) {
      this.currentArticle = result.article;
      return result.article;
    }

    this.currentArticle = result;
    return result;
  }

  /**
   * Get all articles from the catalogue (BlogService compatibility)
   * @returns {Promise<Array>} List of articles
   */
  async getCatalogue() {
    const response = await this.getArticleCatalogue(
      null,
      this.userId,
      this.csrfToken
    );

    // Handle different response formats
    if (response && response.articles && Array.isArray(response.articles)) {
      this.catalogueData = response.articles;
      return response.articles;
    } else if (Array.isArray(response)) {
      this.catalogueData = response;
      return response;
    }

    return [];
  }

  /**
   * Search the catalogue with filters (BlogService compatibility)
   * @param {Object} searchParams - Search parameters
   * @returns {Promise<Array>} Filtered catalogue entries
   */
  async searchCatalogue(searchParams = {}) {
    // Convert BlogService search params to BlogService format
    const searchTerm = searchParams.title || searchParams.url || "";
    const categories = [];

    const result = await this.searchArticles(
      searchTerm,
      categories,
      0,
      1000,
      this.userId,
      this.csrfToken
    );

    // Filter by author if provided
    let articles = result.articles || [];
    if (searchParams.author) {
      articles = articles.filter(
        (article) =>
          article.author &&
          article.author
            .toLowerCase()
            .includes(searchParams.author.toLowerCase())
      );
    }

    return articles;
  }

  /**
   * Get the catalogue data if loaded (BlogService compatibility)
   * @returns {Array|null} Catalogue data or null
   */
  getCatalogueData() {
    return this.catalogueData;
  }

  /**
   * Clear the current article from memory (BlogService compatibility)
   */
  clearCurrentArticle() {
    this.currentArticle = null;
  }

  /**
   * Gets cached search results
   * @param {string} searchTerm - Search term
   * @param {Array} categories - Categories
   * @param {number} page - Page number
   * @param {number} itemsPerPage - Items per page
   * @returns {Object|null} Cached results or null
   */
  getCachedSearch(searchTerm, categories, page, itemsPerPage) {
    const cacheKey = `${searchTerm}_${categories.join(
      ","
    )}_${page}_${itemsPerPage}`;
    return this.searchCache.get(cacheKey) || null;
  }
}

export default new BlogService();
